import './IconButton.css'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { IconName } from '../graphics/iconsAvailable'
import { Icon } from '../graphics/Icon'
import { cn } from '@/utilities/misc'
import { Tooltip } from './Tooltip'
import type { Placement } from '@floating-ui/dom'
import { Link } from 'react-router-dom'

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconName
  tooltip?: ReactNode
  tooltipPlacement?: Placement
  href?: string
  target?: string
}

export function IconButton({
  icon,
  tooltip,
  tooltipPlacement,
  href,
  target,
  className,
  disabled,
  onClick,
  ...rest
}: IconButtonProps) {

  className = cn(
    'IconButton',
    disabled && 'Disabled',
    className,
  )

  function handleClick(ev: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) {
    if (disabled) {
      ev.preventDefault()
      ev.stopPropagation()
      return
    }
    onClick?.(ev as React.MouseEvent<HTMLButtonElement>)
  }

  const label = rest['aria-label'] ?? (typeof tooltip === 'string' ? tooltip : undefined)
  const content = (
    <>
      <Tooltip placement={tooltipPlacement} disabled={disabled}>
        {tooltip}
      </Tooltip>
      <span className='Inliner' />
      <Icon icon={icon} aria-hidden />
    </>
  )

  if (href) {
    return (
      <Link
        to={href}
        target={target}
        className={className}
        onClick={handleClick}
        aria-label={label}
        aria-disabled={disabled}
        {...rest as any}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={handleClick}
      aria-label={label}
      {...rest}
    >
      {content}
    </button>
  )
}
